import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Disclosure, Separator, Typography } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils';
import type { ProductVariant } from '@/api/models';
import type { StockFiguresScope } from '../productFilters/branchScopeMeta';
import { ProductVariantBarcodes } from './ProductVariantBarcodes';
import { ProductVariantBatches } from './ProductVariantBatches';
import { ProductVariantFigures } from './ProductVariantFigures';
import {
    buildVariantLabel,
    formatMoney,
    formatNumber,
    isVariantLowStock,
    variantBarcodeMarkOf,
} from './productDetailMeta';
import { asSpan } from './productDetailTypography';
import { VariantBarcodeMarkChip } from './VariantBarcodeMarkChip';

type ProductVariantPanelProps = {
    variant: ProductVariant;
    isExpanded: boolean;
    onToggle: (variantId: string) => void;
    /** Whose stock the figures count, which the cost, markup and batches name. */
    stockFigures: StockFiguresScope;
    /** Opens the barcodes drawer for this variant (bos-app#224). */
    onEditBarcodes: (variantId: string) => void;
};

/**
 * One variant on the details page (design board **ProductDetail**): a HeroUI
 * Disclosure whose trigger row reads the variant, its barcode
 * (with its Generated or Kept as is mark, bos-app#238), its quantity and its
 * sale price, and whose panel holds its figures
 * (`ProductVariantFigures`), its barcodes card
 * (`ProductVariantBarcodes`, bos-app#224) and its batches. The page
 * owns which variants are open, so the Disclosure is controlled.
 *
 * The row's text is Typography rendered as `span`s, since a `p` isn't allowed
 * inside the trigger button; the sale price takes `tabular-nums` (the
 * registered §6 gap).
 */
export const ProductVariantPanel: React.FC<ProductVariantPanelProps> = ({
    variant,
    isExpanded,
    onToggle,
    stockFigures,
    onEditBarcodes,
}) => {
    const { t } = useTranslation('pages');
    const lowStock = isVariantLowStock(variant);
    const barcodeMark = variantBarcodeMarkOf(variant);

    return (
        <Widget
            className="flex flex-col gap-0 overflow-hidden p-0"
            {...getNodeTestIdProp(`product-variant-${variant.id}`)}
        >
            <Disclosure
                isExpanded={isExpanded}
                onExpandedChange={() => onToggle(variant.id)}
            >
                <Disclosure.Heading>
                    <Disclosure.Trigger
                        className="flex w-full items-center gap-3 px-4 py-3"
                        {...getNodeTestIdProp(
                            `product-variant-toggle-${variant.id}`,
                        )}
                    >
                        {/* First in the row, as the design draws it; the
                            indicator's own `ms-auto` would push the row to
                            the far end. */}
                        <Disclosure.Indicator className="ms-0" />
                        <Typography
                            className="flex-1"
                            render={asSpan}
                            type="body-sm"
                            weight="medium"
                            {...getNodeTestIdProp(
                                `product-variant-label-${variant.id}`,
                            )}
                        >
                            {buildVariantLabel(variant)}
                        </Typography>
                        <Typography
                            className="hidden sm:inline"
                            color="muted"
                            render={asSpan}
                            type="body-xs"
                        >
                            {t('products.detail.variants.barcode')}:{' '}
                            {variant.barcode}
                        </Typography>
                        {barcodeMark ? (
                            <VariantBarcodeMarkChip
                                className="hidden sm:inline-flex"
                                mark={barcodeMark}
                                placement="row"
                                variantId={variant.id}
                            />
                        ) : null}
                        <Chip
                            color={lowStock ? 'warning' : 'success'}
                            size="sm"
                            variant="soft"
                            {...getNodeTestIdProp(
                                `product-variant-stock-${variant.id}`,
                            )}
                        >
                            <Chip.Label>
                                {t('products.detail.variants.quantity')}:{' '}
                                {formatNumber(variant.quantity)}
                            </Chip.Label>
                        </Chip>
                        <Typography
                            className="tabular-nums"
                            render={asSpan}
                            type="body-sm"
                            weight="medium"
                        >
                            {formatMoney(variant.salePriceTetri)}
                        </Typography>
                    </Disclosure.Trigger>
                </Disclosure.Heading>
                <Disclosure.Content>
                    <Separator />
                    <Disclosure.Body
                        className="flex flex-col gap-4 p-2"
                        {...getNodeTestIdProp(
                            `product-variant-detail-${variant.id}`,
                        )}
                    >
                        <ProductVariantFigures
                            stockFigures={stockFigures}
                            variant={variant}
                        />
                        <ProductVariantBarcodes
                            onEdit={onEditBarcodes}
                            variant={variant}
                        />
                        <ProductVariantBatches
                            batches={variant.batches}
                            stockFigures={stockFigures}
                            variantId={variant.id}
                        />
                    </Disclosure.Body>
                </Disclosure.Content>
            </Disclosure>
        </Widget>
    );
};
